/**
 * Permission codes the Reference Data Sets (SetID) screens declare an interest in.
 *
 * These are the dot-strings `server/internal/foundation/refset.go` registers with
 * the RBAC catalogue (CLAUDE.md §6) — grep `PermRefSet`/`PermDeterminant` there for
 * the constants this file mirrors. They live beside the module the way
 * `web/src/modules/org/permissions.ts` does, one file per module, matching the
 * server's own package boundary.
 *
 * FSD 1.2.6.11: "Set definition and determinant declaration require the Enterprise
 * Data Owner role; set assignment additionally requires the accountable business
 * unit manager to confirm the configuration change the unit will experience." The
 * server encodes that as three distinct write permissions rather than one — see
 * `refset.go`'s own comment on `PermRefSetManage`/`PermRefSetAssign`/
 * `PermDeterminantDeclare` — so this file keeps the same three-way split instead of
 * collapsing it into a single "manage" flag a screen could use to hide the
 * distinction.
 */

export const REFERENCE_SET_PERMISSIONS = {
  /**
   * Views the set catalogue, the visibility matrix, set comparisons and
   * reference data resolved for a business unit. Every screen route in this
   * module is gated on this permission — the Internal Audit actor (FSD 1.2.6.1)
   * holds only this and must be able to open every screen here and change
   * nothing.
   */
  read: 'foundation.refset.read',
  /**
   * Creates a reference data set, versions it, and end-dates it — the
   * Enterprise Data Owner's accountability (FSD 1.2.6.1, 1.2.6.5 "+ New
   * Reference Data Set", "Version and Approve", "End-date Set").
   */
  manage: 'foundation.refset.manage',
  /**
   * Declares the determinant that partitions a configuration type (F-RD.001) —
   * the Functional Consultant's accountability.
   */
  determinantDeclare: 'foundation.determinant.declare',
  /**
   * Assigns or reassigns a business unit to a reference data set for one
   * determinant (F-RD.002) — the Business Unit General Manager's confirmation
   * (FSD 1.2.6.5 "Assign Business Units", "Reassign Unit").
   */
  assign: 'foundation.refset.assign',
} as const

export type ReferenceSetPermission =
  (typeof REFERENCE_SET_PERMISSIONS)[keyof typeof REFERENCE_SET_PERMISSIONS]
